// Basic
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import PropTypes from 'prop-types';
// Store & actions
import useSessionStore from '@/app/store/session';
import actionsLogin from '../../../../../app/modules/login/api/actionsLogin';
// Components
import Dropdown from '@/common-utils/components/renders/dropdown/Dropdown';
import ModalUpdatePassword from '@/app/modules/session/components/modals/ModalUpdatePassword';
import ModalSecureAccess from '@/app/modules/session/components/modals/ModalSetMFA/ModalSecureAccess';
import Icon from '@/common-utils/components/renders/icon/Icon';
import Switch from '@/common-utils/components/ui/switch';
import TooltipHelper from '@/common-utils/components/renders/tooltip/TooltipHelper';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { SidebarFooter, SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/common-utils/components/ui/sidebar';
// Commun
import ICONS from '@/common-utils/enums/icons';
import hasRequiredPermissions from '@/common-utils/functions/hasRequiredPermissions';
import HiddenPermissions from '@/app/permissions/HiddenPermissions';
import { usePostHog } from '@posthog/react';

/**
 * @param root0
 * @param root0.dynamicTextColor
 * @param root0.isLight
 * @returns {JSX} SidebarUserFooter
 */
function SidebarUserFooter({ dynamicTextColor, isLight }) {

    // Store
    const { session, storeSession } = useSessionStore();
    const { open, isMobile } = useSidebar();

    // PostHog
    const posthog = usePostHog();

    // State
    const [showModalUpdatePassword, setShowModalUpdatePassword] = useState(false);
    const [showModalSecureAccess, setShowModalSecureAccess] = useState(false);
    const [switchVersion, setSwitchVersion] = useState(true);

    // API Calls
    const queryClient = useQueryClient();
    const onSuccess = () => {
        posthog?.capture('DéconnexionUtilisateur');
        storeSession(null);
        queryClient.clear();
    };
    const onError = () => {
    };
    const submitLogout = actionsLogin.postLogout(onSuccess, onError);

    // Actions
    const handleShowModalUpdatePassword = () => {
        setShowModalUpdatePassword(true);
    };

    const handleShowModalSecureAccess = () => {
        setShowModalSecureAccess(true);
    };

    const hasSwitchVersionPermission = () => {
        return hasRequiredPermissions({
            permissions: session?.permissions,
            requiredPermissions: [HiddenPermissions.TEST_BOMO_BETA.name]
        });
    };

    const handleVersionSwitch = () => {
        setSwitchVersion(false);

        // Use setTimout to let component render completely before refresh
        setTimeout(() => {
            // Set isBeta cookie to false
            document.cookie = 'isBeta=false; path=/';

            // Refresh page
            location.reload();
        }, 100);
    };

    return (
        <>
            <SidebarFooter>
                <SidebarMenu>
                    {hasSwitchVersionPermission() &&
                        <SidebarMenuItem
                        >
                            {!open && (
                                <TooltipHelper
                                    trigger={
                                        <Switch
                                            wrapperClassName='m-0 mb-4'
                                            className='h-5 w-9 data-[state=checked]:bg-success'
                                            thumbClassName='h-3 w-3'
                                            name='switch-version'
                                            isLabelOff={!open}
                                            checked={switchVersion}
                                            onCheckedChange={handleVersionSwitch}
                                            entity=''
                                        />
                                    }
                                    content={<Entity entity='button.switch_version' className='text-sm' />}
                                />
                            )}
                            {open && (
                                <Switch
                                    wrapperClassName='m-0 mb-4'
                                    className='h-5 w-9 data-[state=checked]:bg-success ml-2'
                                    entityClassName='text-sm'
                                    thumbClassName='h-3 w-3'
                                    name='switch-version'
                                    isLabelOff={false}
                                    checked={switchVersion}
                                    onCheckedChange={handleVersionSwitch}
                                    entity='button.switch_version'
                                />
                            )}
                        </SidebarMenuItem>
                    }
                    <SidebarMenuItem data-cy='sidebar-account-actions'>
                        <Dropdown
                            id='user-account-actions'
                            align={isMobile ? 'start' : 'end'}
                            side={isMobile ? 'top' : 'right'}
                            trigger={
                                <SidebarMenuButton
                                    size='lg'
                                    className={
                                        isLight ? 'hover-light button-footer' : 'hover-dark button-footer'
                                    }
                                // onClick={() => setActiveButton('sb-footer-btn')}
                                >

                                    <div className='flex aspect-square size-8 items-center justify-center rounded-lgtext-sidebar-primary-foreground'>
                                        <Icon icon={ICONS.ACCOUNT} className={'!text-4xl'} />
                                    </div>
                                    <div className='grid flex-1 text-left text-sm leading-tight'>
                                        <span className={'truncate font-semibold'} style={{ color: dynamicTextColor }}>{session.email.split('@')[0]}</span>
                                    </div>
                                </SidebarMenuButton>
                            }
                            actions={[
                                {
                                    icon: ICONS.USER_MANAGE_ACCOUNT,
                                    entity: 'sidebar.actions.manage_user_account',
                                    onClick: handleShowModalUpdatePassword,
                                    visible: true
                                },
                                {
                                    icon: ICONS.SECURITY,
                                    entity: 'sidebar.actions.secure_access',
                                    onClick: handleShowModalSecureAccess,
                                    visible: true
                                },
                                {
                                    icon: ICONS.LOGOUT,
                                    entity: 'sidebar.actions.login_sign_out',
                                    onClick: () => {
                                        submitLogout.mutate();
                                    },
                                    visible: true
                                    // disabled: this.props.isLoadingUser
                                }
                            ]}
                        />
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
            {
                showModalUpdatePassword &&
                <ModalUpdatePassword
                    isOpen={showModalUpdatePassword}
                    setIsOpen={setShowModalUpdatePassword}
                />
            }
            {
                showModalSecureAccess &&
                <ModalSecureAccess
                    isOpen={showModalSecureAccess}
                    setIsOpen={setShowModalSecureAccess}
                />
            }
        </>
    );
}

SidebarUserFooter.propTypes = {
    dynamicTextColor: PropTypes.string.isRequired,
    isLight: PropTypes.bool.isRequired
};

export default SidebarUserFooter;